import React, { useEffect, useCallback, useRef } from 'react';
import { Upload, AlertCircle } from 'lucide-react';
import useTemplateEditor from './hooks/useTemplateEditor';
import useFieldPlacement from './hooks/useFieldPlacement';
import PDFViewer from './PDFViewer';
import FieldPalette from './FieldPalette';
import FieldOverlay from './FieldOverlay';
import SubmitterRolesManager from './SubmitterRolesManager';
import PageNavigator from './PageNavigator';
import FieldPropertiesPanel from './FieldPropertiesPanel';

const TemplateEditor = ({
  initialTemplate = null,
  onSave,
  onCancel,
  isSaving = false,
}) => {
  const fileInputRef = useRef(null);

  // Main editor state
  const editor = useTemplateEditor(initialTemplate);

  // Field placement interactions
  const placement = useFieldPlacement({
    pageDimensions: editor.pageDimensions,
    onAddField: editor.addField,
    onMoveField: editor.moveField,
    onResizeField: editor.resizeField,
    onSelectField: editor.selectField,
  });

  // Attach global mouse event listeners for drag/resize
  useEffect(() => {
    const handleMouseMove = (e) => placement.handleMouseMove(e);
    const handleMouseUp = () => placement.handleMouseUp();

    if (placement.isDragging || placement.isResizing) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [placement.isDragging, placement.isResizing, placement]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Delete selected field
      if ((e.key === 'Delete' || e.key === 'Backspace') && editor.selectedFieldId) {
        // Don't delete if we're in an input
        if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') {
          return;
        }
        e.preventDefault();
        editor.deleteField(editor.selectedFieldId);
      }

      // Duplicate selected field (Ctrl+D or Cmd+D)
      if ((e.ctrlKey || e.metaKey) && e.key === 'd' && editor.selectedFieldId) {
        e.preventDefault();
        editor.duplicateField(editor.selectedFieldId);
      }

      // Deselect (Escape)
      if (e.key === 'Escape') {
        editor.deselectField();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [editor]);

  // File upload handler
  const handleFileUpload = useCallback((e) => {
    const file = e.target.files?.[0];
    if (file && file.type === 'application/pdf') {
      editor.loadPDF(file);
    } else if (file) {
      editor.setError('Please upload a PDF file');
    }
  }, [editor]);

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  // Handle drop on PDF
  const handleDrop = useCallback((e, containerRect) => {
    placement.handleCanvasDrop(e, containerRect);
  }, [placement]);

  // Handle save
  const handleSave = useCallback(async () => {
    try {
      const payload = editor.mode === 'edit'
        ? editor.buildUpdatePayload()
        : editor.buildTemplatePayload();

      if (onSave) {
        await onSave(payload, editor.mode, editor.templateId);
      }
    } catch (err) {
      editor.setError(err.message);
    }
  }, [editor, onSave]);

  const hasPdf = editor.pdfFile || editor.pdfUrl;

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex-shrink-0 p-4 border-b border-gray-200 bg-gray-50">
        <div className="flex items-center gap-4">
          <div className="flex-1">
            <label className="block text-xs font-medium text-gray-600 mb-1">
              Template Name *
            </label>
            <input
              type="text"
              value={editor.templateName}
              onChange={(e) => editor.setTemplateName(e.target.value)}
              placeholder="e.g., Retainer Agreement"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
            />
          </div>
          <div className="w-48">
            <label className="block text-xs font-medium text-gray-600 mb-1">
              Folder (optional)
            </label>
            <input
              type="text"
              value={editor.folderName}
              onChange={(e) => editor.setFolderName(e.target.value)}
              placeholder="e.g., Legal Documents"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
            />
          </div>
        </div>
      </div>

      {/* Error display */}
      {editor.error && (
        <div className="flex-shrink-0 mx-4 mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-center gap-2 text-red-700">
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
            <span className="text-sm">{editor.error}</span>
            <button
              onClick={() => editor.setError(null)}
              className="ml-auto text-red-500 hover:text-red-700"
            >
              &times;
            </button>
          </div>
        </div>
      )}

      {/* Main content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left sidebar */}
        <div className="w-64 flex-shrink-0 p-4 space-y-4 overflow-y-auto border-r border-gray-200 bg-gray-50">
          {/* PDF Upload */}
          {!hasPdf && (
            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <h3 className="text-sm font-semibold text-gray-700 mb-3">Document</h3>
              <input
                ref={fileInputRef}
                type="file"
                accept="application/pdf"
                onChange={handleFileUpload}
                className="hidden"
              />
              <button
                onClick={handleUploadClick}
                className="w-full flex flex-col items-center gap-2 p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-yellow-400 hover:bg-yellow-50 transition-colors"
              >
                <Upload className="w-8 h-8 text-gray-400" />
                <span className="text-sm text-gray-600">Upload PDF</span>
              </button>
            </div>
          )}

          {/* Field palette */}
          <FieldPalette
            onDragStart={placement.handlePaletteDragStart}
            selectedToolType={placement.selectedToolType}
            onToolSelect={placement.setSelectedToolType}
            disabled={!hasPdf}
          />

          {/* Submitter roles */}
          <SubmitterRolesManager
            roles={editor.roles}
            activeRoleUuid={editor.activeRoleUuid}
            onSetActiveRole={editor.setActiveRoleUuid}
            onAddRole={editor.addRole}
            onUpdateRole={editor.updateRole}
            onRemoveRole={editor.removeRole}
          />
        </div>

        {/* Center: PDF Viewer */}
        <div className="flex-1 flex flex-col overflow-hidden bg-gray-100">
          {hasPdf ? (
            <>
              <PDFViewer
                pdfFile={editor.pdfFile}
                pdfUrl={editor.pdfUrl}
                currentPage={editor.currentPage}
                onDocumentLoad={editor.handleDocumentLoad}
                onPageLoad={editor.handlePageLoad}
                onDragOver={placement.handleCanvasDragOver}
                onDrop={handleDrop}
                onPageClick={placement.handleCanvasClick}
                className="flex-1 overflow-auto"
              >
                {/* Field overlays */}
                {editor.currentPageFields.map((field) => (
                  <FieldOverlay
                    key={field.uuid}
                    field={field}
                    role={editor.roles.find(r => r.uuid === field.roleUuid)}
                    pageWidth={editor.pageDimensions.width}
                    pageHeight={editor.pageDimensions.height}
                    isSelected={editor.selectedFieldId === field.uuid}
                    isDragging={placement.draggedFieldId === field.uuid && placement.isDragging}
                    onMouseDown={placement.handleFieldMouseDown}
                    onResizeStart={placement.handleFieldMouseDown}
                  />
                ))}
              </PDFViewer>

              {/* Page navigator */}
              <div className="flex-shrink-0 border-t border-gray-200 bg-white">
                <PageNavigator
                  currentPage={editor.currentPage}
                  totalPages={editor.totalPages}
                  onPreviousPage={editor.goToPreviousPage}
                  onNextPage={editor.goToNextPage}
                  onGoToPage={editor.goToPage}
                />
              </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center text-gray-500">
                <Upload className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                <p className="text-lg font-medium">Upload a PDF to get started</p>
                <p className="text-sm mt-1">Then drag fields onto the document</p>
              </div>
            </div>
          )}
        </div>

        {/* Right sidebar */}
        <div className="w-72 flex-shrink-0 p-4 overflow-y-auto border-l border-gray-200 bg-gray-50">
          <FieldPropertiesPanel
            field={editor.selectedField}
            roles={editor.roles}
            onUpdateField={editor.updateField}
            onDeleteField={editor.deleteField}
            onDuplicateField={editor.duplicateField}
          />

          {/* Validation errors */}
          {editor.validationErrors.length > 0 && (
            <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <h4 className="text-sm font-medium text-amber-800 mb-2">
                Before saving:
              </h4>
              <ul className="text-xs text-amber-700 space-y-1">
                {editor.validationErrors.map((err, i) => (
                  <li key={i}>• {err}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Field count */}
          <div className="mt-4 p-3 bg-gray-100 rounded-lg">
            <div className="text-xs text-gray-600">
              <div className="flex justify-between">
                <span>Total fields:</span>
                <span className="font-medium">{editor.fields.length}</span>
              </div>
              <div className="flex justify-between mt-1">
                <span>On this page:</span>
                <span className="font-medium">{editor.currentPageFields.length}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="flex-shrink-0 flex items-center justify-end gap-3 p-4 border-t border-gray-200 bg-gray-50">
        <button
          onClick={onCancel}
          className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500"
        >
          Cancel
        </button>
        <button
          onClick={handleSave}
          disabled={!editor.isValid || isSaving}
          className="px-4 py-2 text-sm font-medium text-white bg-yellow-500 border border-transparent rounded-lg hover:bg-yellow-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isSaving ? 'Saving...' : editor.mode === 'edit' ? 'Update Template' : 'Create Template'}
        </button>
      </div>
    </div>
  );
};

export default TemplateEditor;
